/**
 * Copyright © 2026 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

import { invalidateAuditLogQueries } from "../../../utils/invalidateAuditLogQueries";
import React, { useState, useEffect, useContext, useMemo, useCallback } from "react";
import { NavigableBreadcrumbLink } from "../../../components/page_breadcrumbs/navigable_breadcrumb_link";
import api from "../../../api";
import authenticatedFetch from "../../../api/authenticatedFetch";
import dayjs from "dayjs";
import {
  Box,
  Breadcrumbs,
  Button,
  Chip,
  FormControl,
  IconButton,
  InputAdornment,
  Select,
  Typography
} from "@mui/material";
import CachedIcon from "@mui/icons-material/Cached";
import { StyledBreadCrumbContainer, StyledInputLabel, StyledLink } from "../employee_visibility/styles";
import { selectBaseStyles, viewTypographyStyles } from "../../audit_logs/styles";
import { SelectedMenuItem } from "../../../components/selected_menu_item";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { datePickerStyles } from "../employee_insights/styles";
import DropdownSearchBar from "../../../components/dorpdown_searchbar";
import { DropdownInfiniteLoader } from "../../../components/dropdown_infinite_loader";
import { keepPreviousData, useInfiniteQuery, useQuery, useQueryClient } from "@tanstack/react-query";
import ClearIcon from '@mui/icons-material/Clear';
import { ResetButton } from "../../../components/reset_button";
import { UpdateButton } from "../../../components/update_button";
import { MenuHeading } from "../../../components/menu_heading";
import camelize from "../../../utils/camelize";
import { IndexTable } from "../../../components/index_table";
import { Loader } from "../../../components/loader";
import { RenderJobList } from "../../../components/render_job_list";
import { ActionContainer } from "../../../components/action_container";
import { LoadingSpinner } from "../../../components/loading_spinner";
import { useNotificationContext } from "../../../contexts/notificationContext";
import { checkAdmin } from "../../../utils/checkAdmin";
import { useSelector } from "react-redux";
import { strictCanScope } from "../../../utils/caslStrict";
import { AbilityContext } from "../../../contexts/abilityContext";
import { useLocation } from "react-router-dom";
import moment from "moment";
import { buildQueryParams } from "../../../utils/buildQueryParams";
import { FilterContainer } from "../../../components/filter_container";
import { StyledMenuItem } from "../../../components/styled_menu_item";
import { hasSingleItem } from "../../../utils/hasSingleItem";
import { getSingleItemValue } from "../../../utils/getSingleItemValue";
import { Query_Keys } from "../../../lib/queryKeys";
import { useOtMutation } from "../../../hooks/mutations/useOtMutation";
import { reportMutationKeyGroups } from "../../../hooks/mutations/queryKeyGroups/reportMutationKeys";
import { PerPage } from "../../../constants/perPage";
import {
  getDropdownNextPageParam,
  hasDisplayableDropdownValue,
  mergeUniqueByKey,
  normalizeEmployee,
  normalizeRestaurant,
  normalizeRestaurantGroup,
} from "../../../utils/dropdownInfiniteQuery";
import { useDebounce } from "../../../utils/useDebounce";
import { useInfiniteScrollObserver } from "../../../hooks/useInfiniteScrollObserver";
import { isValidDayjsValue } from "../../../utils/isValidDayjsValue";
import { isValidCustomDateRange } from "../../../utils/isValidCustomDateRange";

const ALL_VALUE = 'all';
const defaultFilter = {
  dateFilter: 'Today',
  has_ot_discount: ALL_VALUE,
  restaurant_group_id: ALL_VALUE,
  restaurant_ids: [ALL_VALUE],
  network_guest_name: ALL_VALUE,
  customDateFrom: null,
  customDateTo: null,
}

const RenderList = React.memo(({ data }) => {
  return (
    <Box>
      <ul style={{ padding: 0 }}>
        {data?.map((item, index) => (
          <li key={index} style={{ listStyle: "none" }}>
            {item}
          </li>
        ))}
      </ul>
    </Box>
  );
});

RenderList.displayName = 'RenderList'

export const EmployeeTimeEntries = () => {
  const [filtersChanged, setFiltersChanged] = useState(false);
  const [restaurantSearch, setRestaurantSearch] = useState('');
  const [restaurantGroupSearch, setRestaurantGroupSearch] = useState('');
  const [employeeSearch, setEmployeeSearch] = useState("");
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [page, setPage] = useState(1);
  const [fetchPermission, setFetchPermission] = useState(false)
  const [filters, setFilters] = useState(defaultFilter)
  const [appliedFilters, setAppliedFilters] = useState(defaultFilter)
  const showNotification = useNotificationContext();
  const queryClient = useQueryClient();
  const [isLoading, setIsLoading] = useState(false)
  const [csvLoading, setCsvLoading] = useState(false)
  const [sortBy, setSortBy] = useState('')
  const [sortOrder, setSortOrder] = useState('desc')
  const [restaurantOptionsCache, setRestaurantOptionsCache] = useState([])
  const [restaurantGroupOptionsCache, setRestaurantGroupOptionsCache] = useState([])
  const [employeeOptionsCache, setEmployeeOptionsCache] = useState([])
  const { admin } = useSelector((state) => state.admin);
  const superAdmin = checkAdmin(admin?.formatted_roles)
  const { ability } = useContext(AbilityContext);
  const location = useLocation()
  let navigatedRestaurant = location?.state?.restaurant
  const navigatedDate = location?.state?.date
  const debouncedRestaurantGroupSearch = useDebounce(restaurantGroupSearch.trim(), 300)
  const debouncedRestaurantSearch = useDebounce(restaurantSearch.trim(), 300)
  const debouncedEmployeeSearch = useDebounce(employeeSearch.trim(), 300)

  const formatTime = useCallback((date, timezone) => {
    if (!date || !timezone) return '-'

    try {
      const formatted = moment.parseZone(date).format("h:mm A");
      return `${formatted} (${timezone})`;
    } catch (err) {
      return "-";
    }
  }, []);

  const formatDecimal = useCallback((value) => {
    if (value === null || value === undefined || value === '' || isNaN(Number(value))) return '-'

    return Number(value).toFixed(2)
  }, []);

  const formatCurrency = useCallback((value) => {
    const formatted = formatDecimal(value)
    return formatted === '-' ? formatted : `$${formatted}`
  }, [formatDecimal]);

  const getDiscountLabel = useCallback((data) => {
    if (data === true) {
      return 'OwnersTable'
    }
    else {
      return 'All'
    }
  }, []);

  useEffect(() => {
    if (navigatedRestaurant?.name) {
      const date = new Date(navigatedDate)
      const redirectedFilters = {
        ...defaultFilter,
        restaurant_ids: [navigatedRestaurant?.id],
        dateFilter: 'Custom Date',
        customDateFrom: dayjs(date),
        customDateTo: dayjs(date),
      }

      setFilters(prevFilters => ({
        ...prevFilters,
        ...redirectedFilters,
      }));
      setAppliedFilters(redirectedFilters)
      setFiltersChanged(false)
      setItemsPerPage(25)
      setPage(1)
      setFetchPermission(strictCanScope(ability, 'fetch_and_imports', 'Restaurant', `${navigatedRestaurant?.id}`))
      navigatedRestaurant = null
    }
  }, [navigatedRestaurant, navigatedDate, ability])

  const Days = useMemo(() => [
    "All",
    "Today",
    "Yesterday",
    "This Week",
    "Last 7 Days",
    "This Month",
    "Last Month",
    "Custom Date",
  ], []);

  const buildFormattedFilters = useCallback((sourceFilters = filters) => {
    const params = {};
    const today = dayjs();

    // Date range handling
    if (sourceFilters?.dateFilter !== "Custom Date") {
      switch (sourceFilters.dateFilter) {
        case "Today":
          params.date_range = {
            from: today.format("DD/MM/YYYY"),
            to: today.format("DD/MM/YYYY"),
          };
          break;
        case "Last 7 Days":
          params.date_range = {
            from: today.subtract(7, "day").format("DD/MM/YYYY"),
            to: today.format("DD/MM/YYYY"),
          };
          break;
        case "Yesterday":
          params.date_range = {
            from: today.subtract(1, "day").format("DD/MM/YYYY"),
            to: today.subtract(1, "day").format("DD/MM/YYYY"),
          };
          break;
        case "This Week":
          params.date_range = {
            from: today.startOf("week").format("DD/MM/YYYY"),
            to: today.endOf("week").format("DD/MM/YYYY"),
          };
          break;
        case "This Month":
          params.date_range = {
            from: today.startOf("month").format("DD/MM/YYYY"),
            to: today.endOf("month").format("DD/MM/YYYY"),
          };
          break;
        case "Last Month":
          params.date_range = {
            from: today
              .subtract(1, "month")
              .startOf("month")
              .format("DD/MM/YYYY"),
            to: today.subtract(1, "month").endOf("month").format("DD/MM/YYYY"),
          };
          break;
        default:
          break;
      }
    } else if (sourceFilters?.customDateFrom && sourceFilters.customDateTo) {
      params.date_range = {
        from: dayjs(sourceFilters.customDateFrom).format("DD/MM/YYYY"),
        to: dayjs(sourceFilters.customDateTo).format("DD/MM/YYYY"),
      };
    }

    if (sourceFilters?.network_guest_name !== ALL_VALUE) {
      params.network_guest_name = sourceFilters.network_guest_name;
    }

    if (!sourceFilters?.restaurant_ids?.includes(ALL_VALUE)) {
      params.restaurant_ids = sourceFilters.restaurant_ids;
    }

    if (sourceFilters?.restaurant_group_id !== ALL_VALUE) {
      params.restaurant_group_id = sourceFilters.restaurant_group_id;
    }

    if (sourceFilters?.has_ot_discount !== ALL_VALUE) {
      params.has_ot_discount = sourceFilters.has_ot_discount;
    }

    return params;
  }, [filters]);

  const fetchRestaurantGroupList = async ({ pageParam = 1 }) => {
    const response = await api.get("/v1/admin/dropdowns/restaurant_groups_list", {
      params: {
        page: pageParam,
        per_page: PerPage,
        search: debouncedRestaurantGroupSearch || undefined,
      },
    });
    return response?.data?.map(normalizeRestaurantGroup) || [];
  };

  const {
    data: restaurantGroups = [],
    isFetching: restaurantGroupFetching,
    isFetchingNextPage: restaurantGroupsFetchingNextPage,
    fetchNextPage: fetchNextRestaurantGroupsPage,
    hasNextPage: hasNextRestaurantGroupsPage,
  } = useInfiniteQuery({
    queryKey: Query_Keys.restaurant_groups_dropdown({ search: debouncedRestaurantGroupSearch }),
    queryFn: fetchRestaurantGroupList,
    initialPageParam: 1,
    getNextPageParam: getDropdownNextPageParam,
    select: (data) => data.pages.flat(),
    staleTime: 10 * 60 * 1000,
    placeholderData: keepPreviousData,
  });

  const fetchRestaurants = async ({ pageParam = 1 }) => {
    const params = {};
    if (filters?.restaurant_group_id !== ALL_VALUE) {
      params.restaurant_group_ids = filters?.restaurant_group_id;
    }
    params.page = pageParam;
    params.per_page = PerPage;
    params.search = debouncedRestaurantSearch || undefined;
    let res = await api.get("/v1/admin/dropdowns/restaurants_list", { params });
    return res?.data?.map(normalizeRestaurant) || [];
  };

  const {
    data: restaurants = [],
    isFetching: restaurantFetching,
    isFetchingNextPage: restaurantsFetchingNextPage,
    fetchNextPage: fetchNextRestaurantsPage,
    hasNextPage: hasNextRestaurantsPage,
  } = useInfiniteQuery({
    queryKey: Query_Keys.restaurants_dropdown({
      restaurant_group_id: filters?.restaurant_group_id,
      search: debouncedRestaurantSearch,
    }),
    queryFn: fetchRestaurants,
    initialPageParam: 1,
    getNextPageParam: getDropdownNextPageParam,
    select: (data) => data.pages.flat(),
    staleTime: 10 * 60 * 1000,
    enabled: true,
    placeholderData: keepPreviousData,
  });

  const fetchEmployeeList = async ({ pageParam = 1 }) => {
    const params = {};
    if (!filters?.restaurant_ids?.includes(ALL_VALUE)) {
      params.restaurant_id = filters?.restaurant_ids;
    }
    if (filters?.restaurant_group_id !== ALL_VALUE) {
      params.restaurant_group_id = filters?.restaurant_group_id;
    }
    params.page = pageParam;
    params.per_page = PerPage;
    params.search = debouncedEmployeeSearch || undefined;
    const response = await api.get('/v1/admin/dropdowns/active_employees_list', { params })
    return response?.data
      ?.map(normalizeEmployee)
      ?.filter((employee) => (
        hasDisplayableDropdownValue(employee?.label)
        && hasDisplayableDropdownValue(employee?.value)
      )) || []
  }

  const {
    data: employeesList = [],
    isFetching: employeeFetching,
    isFetchingNextPage: employeesFetchingNextPage,
    fetchNextPage: fetchNextEmployeesPage,
    hasNextPage: hasNextEmployeesPage,
  } = useInfiniteQuery({
    queryKey: Query_Keys.employees_dropdown({
      restaurant_group_id: filters?.restaurant_group_id,
      restaurant_id: filters?.restaurant_ids,
      search: debouncedEmployeeSearch,
    }),
    queryFn: fetchEmployeeList,
    initialPageParam: 1,
    getNextPageParam: getDropdownNextPageParam,
    select: (data) => data.pages.flat(),
    staleTime: 10 * 60 * 1000,
    placeholderData: keepPreviousData,
  })

  const { rootRef: restaurantGroupMenuRef, targetRef: restaurantGroupLoaderRef } = useInfiniteScrollObserver({
    fetchNextPage: fetchNextRestaurantGroupsPage,
    hasNextPage: hasNextRestaurantGroupsPage,
    isFetchingNextPage: restaurantGroupsFetchingNextPage,
  })

  const { rootRef: restaurantMenuRef, targetRef: restaurantLoaderRef } = useInfiniteScrollObserver({
    fetchNextPage: fetchNextRestaurantsPage,
    hasNextPage: hasNextRestaurantsPage,
    isFetchingNextPage: restaurantsFetchingNextPage,
  })

  const { rootRef: employeeMenuRef, targetRef: employeeLoaderRef } = useInfiniteScrollObserver({
    fetchNextPage: fetchNextEmployeesPage,
    hasNextPage: hasNextEmployeesPage,
    isFetchingNextPage: employeesFetchingNextPage,
  })

  const fetchEmployeeTimeEntriesList = useCallback(async () => {
    const data = buildFormattedFilters(appliedFilters)
    const response = await api.get("/v1/admin/reports/employee_time_entries", {
      params: {
        per_page: itemsPerPage,
        page: page,
        ...data,
        ...(sortBy ? { sort_by: sortBy, sort_order: sortOrder } : {}),
      },
    });
    return { data: response?.data, pageCount: response?.headers["total-count"] }
  }, [buildFormattedFilters, appliedFilters, itemsPerPage, page, sortBy, sortOrder])

  const { data: employeeTimeEntries, isFetching } = useQuery({
    queryKey: Query_Keys.employee_time_entries({
      queryFilter: appliedFilters,
      page: page,
      itemsPerPage: itemsPerPage,
      sortBy,
      sortOrder,
    }),
    queryFn: fetchEmployeeTimeEntriesList,
    staleTime: 10 * 60 * 1000,
    placeholderData: keepPreviousData,
  })

  useEffect(() => {
    setRestaurantOptionsCache((currentOptions) => mergeUniqueByKey(currentOptions, restaurants, 'id'));
  }, [restaurants]);

  useEffect(() => {
    setRestaurantGroupOptionsCache((currentOptions) => mergeUniqueByKey(currentOptions, restaurantGroups, 'label'));
  }, [restaurantGroups]);

  useEffect(() => {
    setEmployeeOptionsCache((currentOptions) => mergeUniqueByKey(currentOptions, employeesList, 'value'));
  }, [employeesList]);

  const hasSingleAvailableRestaurantGroup = !debouncedRestaurantGroupSearch && hasSingleItem(restaurantGroups);
  const hasSingleAvailableRestaurant = !debouncedRestaurantSearch && hasSingleItem(restaurants);
  const hasSingleAvailableEmployee = !debouncedEmployeeSearch && hasSingleItem(employeesList);

  useEffect(() => {
    if (hasSingleAvailableRestaurantGroup) {
      const singleRestaurantGroup = getSingleItemValue(restaurantGroups);
      setFilters(prev => ({
        ...prev,
        restaurant_group_id: singleRestaurantGroup.label
      }));
    }
  }, [hasSingleAvailableRestaurantGroup, restaurantGroups]);

  useEffect(() => {
    if (hasSingleAvailableRestaurant) {
      const singleRestaurant = getSingleItemValue(restaurants);
      setFilters(prev => ({
        ...prev,
        restaurant_ids: [singleRestaurant.id]
      }));
    }
  }, [hasSingleAvailableRestaurant, restaurants]);

  useEffect(() => {
    if (hasSingleAvailableEmployee) {
      const singleEmployee = getSingleItemValue(employeesList);
      setFilters(prev => ({
        ...prev,
        network_guest_name: singleEmployee.value
      }));
    }
  }, [employeesList, hasSingleAvailableEmployee]);

  const handleRestaurantGroupChange = useCallback((event) => {
    const value = event.target.value;
    setFilters(prev => ({
      ...prev,
      restaurant_group_id: value,
      restaurant_ids: [ALL_VALUE]
    }))
    setFiltersChanged(true)
  }, [])

  const handleRestaurantChange = useCallback((event) => {
    const {
      target: { value },
    } = event;

    if ((value?.length === 1 && value.includes(ALL_VALUE)) || value?.length === 0) {
      setFilters(prev => ({ ...prev, restaurant_ids: [ALL_VALUE] }));
    }
    else {
      const newRestaurantIds = value?.filter(val => val !== ALL_VALUE);
      setFilters(prev => ({ ...prev, restaurant_ids: newRestaurantIds }));
    }

    setFiltersChanged(true);

    const selectedIds = value?.includes(ALL_VALUE) ? [ALL_VALUE] : value;
    checkFetchPermission(selectedIds.filter(id => id !== ALL_VALUE));
  }, [restaurants])

  const handleEmployeeChange = useCallback((event) => {
    setFilters(prev => ({ ...prev, network_guest_name: event.target.value }));
    setFiltersChanged(true);
  }, [])

  const handleFilterChange = useCallback((field, value) => {
    setFilters((prev) => {
      if (field === "dateFilter") {
        return {
          ...prev,
          dateFilter: value,
          customDateFrom: null,
          customDateTo: null,
        };
      }

      if (field === "customDateFrom") {
        if (!value) {
          return {
            ...prev,
            customDateFrom: null,
            customDateTo: null,
          };
        }

        return {
          ...prev,
          customDateFrom: value,
          customDateTo: isValidDayjsValue(value) && !isValidDayjsValue(prev.customDateTo)
            ? dayjs().startOf("day")
            : prev.customDateTo,
        };
      }

      return { ...prev, [field]: value };
    });
    setFiltersChanged(true);
  }, []);

  const isCustomDateRangeValid =
    filters?.dateFilter !== "Custom Date" ||
    isValidCustomDateRange(filters?.customDateFrom, filters?.customDateTo);

  const isUpdateDisabled =
    filters?.dateFilter === "Custom Date" &&
    !isCustomDateRangeValid;

  const handleOwnersTableDiscount = useCallback((e) => {
    setFilters((prev) => ({ ...prev, has_ot_discount: e.target.value }));
    setFiltersChanged(true);
  }, [])

  const handleRestaurantClear = useCallback(() => {
    setFilters(prev => ({ ...prev, restaurant_ids: [ALL_VALUE] }));
    setFiltersChanged(true);
    setFetchPermission(false);
  }, [])

  const handleRestaurantGroupClear = useCallback(() => {
    setFilters(prev => ({
      ...prev,
      restaurant_group_id: ALL_VALUE,
      restaurant_ids: [ALL_VALUE]
    }))
    setFiltersChanged(true);
    setFetchPermission(false);
  }, [])

  const handleEmployeeClear = useCallback(() => {
    setFilters(prev => ({ ...prev, network_guest_name: ALL_VALUE }));
    setFiltersChanged(true)
  }, [])

  const handleChange =() => {
    setFiltersChanged(false)
    setAppliedFilters(filters)
    setItemsPerPage(25);
    setPage(1);
  }

  const handleReset = useCallback(() => {
    setFiltersChanged(false)
    setAppliedFilters(defaultFilter)
    setItemsPerPage(25);
    setPage(1);
    setFilters(defaultFilter);
    setFetchPermission(false);
  }, [])

  const getRestaurantGroupName = useCallback((id) => {
    if (id === ALL_VALUE) return 'All';
    const restaurantGroup = restaurantGroupOptionsCache?.find((res) => res.label === id)
    return restaurantGroup?.value ? restaurantGroup?.value : ''
  }, [restaurantGroupOptionsCache])

  const getEmployeeName = useCallback((id) => {
    if (id === ALL_VALUE) return 'All';
    const employee = employeeOptionsCache?.find((employee) => employee.value === id)
    return employee ? employee?.label : ''
  }, [employeeOptionsCache])

  const getRestaurantDisplayValue = useCallback(() => {
    if (!filters?.restaurant_ids || filters?.restaurant_ids?.length === 0 ||
        (filters?.restaurant_ids?.includes(ALL_VALUE) && filters?.restaurant_ids?.length === 1)) {
      return 'All';
    }

    if (filters?.restaurant_ids?.length === 1 && restaurantOptionsCache) {
      const restaurant = restaurantOptionsCache.find(r => r.id === filters.restaurant_ids[0]);
      return restaurant ? restaurant.name : 'All';
    }

    return `${filters?.restaurant_ids?.length} Restaurants Selected`;
  }, [filters?.restaurant_ids, restaurantOptionsCache]);

  const { mutateAsync: fetchTimeEntriesMutation } = useOtMutation({
    mutationFn: async (payload) => await api.post('/v1/admin/employees/fetch_employee_time_entries', payload),
    invalidateKeys: [...reportMutationKeyGroups.fetchTimeEntries],
    errorMessage: () => null,
    onSuccess: (data, variables) => {
      setPage(1)
      setFiltersChanged(false)
      setIsLoading(false)
    },
    onError: (error, variables) => {
      console.log(error, `Error`)
      setIsLoading(false)
    },
    onSettled: () => {
      invalidateAuditLogQueries(queryClient);
    }
  });

  const fetchData = useCallback(async () => {
    let fetchRestaurants = filters?.restaurant_ids || []
    let fetchRestaurantsList = []

    if (fetchRestaurants.includes(ALL_VALUE)) {
      if (restaurants && restaurants.length > 0) {
        fetchRestaurantsList = restaurants;
      } else {
        showNotification('No restaurants available to fetch', 'error');
        return;
      }
    } else {
      fetchRestaurants?.forEach((id) => {
        const restaurant = restaurants?.find(r => r.id === id);
        if (restaurant) {
          fetchRestaurantsList.push(restaurant);
        }
      });
    }

    if (fetchRestaurantsList.length === 0) {
      showNotification('Select a restaurant', 'error');
      return;
    }

    const getDate = () => {
      let date_range = null
      const today = dayjs();
      if (filters?.dateFilter !== "Custom Date" && filters?.dateFilter !== "All") {
        switch (filters?.dateFilter) {
          case "Today":
            return date_range = {
              from: today.format("YYYY-MM-DD"),
              to: today.add(1, 'day').format("YYYY-MM-DD"),
            };
          case "Yesterday":
            return date_range = {
              from: today.subtract(1, "day").format("YYYY-MM-DD"),
              to: today.format("YYYY-MM-DD"),
            };
          case "This Week":
            return date_range = {
              from: today.startOf("week").format("YYYY-MM-DD"),
              to: today.endOf("week").format("YYYY-MM-DD"),
            };
          case "Last 7 Days":
            return date_range = {
              from: today.subtract(7, "day").format("YYYY-MM-DD"),
              to: today.format("YYYY-MM-DD"),
            };
          case "This Month":
            return date_range = {
              from: today.startOf("month").format("YYYY-MM-DD"),
              to: today.endOf("month").format("YYYY-MM-DD"),
            }
          case "Last Month":
            return date_range = {
              from: today
                .subtract(1, "month")
                .startOf("month")
                .format("YYYY-MM-DD"),
              to: today.subtract(1, "month").endOf("month").format("YYYY-MM-DD"),
            };
          default:
            return null;
        }
      }
      else if (
        filters?.dateFilter === "Custom Date" &&
        isValidCustomDateRange(filters?.customDateFrom, filters?.customDateTo)
      ) {
        return date_range = {
          from: dayjs(filters?.customDateFrom).format("YYYY-MM-DD"),
          to: dayjs(filters?.customDateTo).format("YYYY-MM-DD"),
        };
      }
      return null;
    }

    const fetchDetails = async (restaurant, startDate, endDate) => {
      const restaurantName = restaurant?.name;
      setIsLoading(true)
      try {
        await fetchTimeEntriesMutation(
          {
            restaurant_id: restaurant?.id,
            start_date: startDate,
            end_date: endDate
          },
          {
            onSuccess: () => {
              const name = restaurantName || 'selected restaurant';
              showNotification(`Employee Time Entries Fetched Successfully for ${name}`, 'success');
            },
            onError: () => {
              const name = restaurantName || 'selected restaurant';
              showNotification(`Failed to fetch employee time entries for ${name}`, 'error');
            }
          }
        );
      } catch {
        // Handled in onError
      }
    }

    let noPermissionRestaurant = []
    let hasPermission = false;

    for (const restaurant of fetchRestaurantsList) {
      setIsLoading(true)
      if (superAdmin?.role === 'admin' || strictCanScope(ability, 'fetch_and_imports', 'Restaurant', `${restaurant?.id}`)) {
        let date = getDate()
        await fetchDetails(restaurant, `${date?.from}T00:00:00.000Z`, `${date?.to}T23:59:59.000Z`);
        setIsLoading(false)
        hasPermission = true;
      }
      else {
        console.log(`You don't have fetch permissions for ${restaurant?.name}`);
        noPermissionRestaurant.push(`${restaurant?.name}`)
        setIsLoading(false)
      }
    }

    if (!hasPermission && noPermissionRestaurant.length === 0) {
      showNotification('You do not have fetch permissions for any selected restaurant', 'error');
    } else if (noPermissionRestaurant?.length > 0) {
      setTimeout(() => {
        showNotification(`You don't have fetch permissions for ${noPermissionRestaurant.toString()}`, 'error')
      }, [5000])
    }
  }, [filters, restaurants, ability]);

  const checkFetchPermission = useCallback((fetchRestaurants) => {
    if (!fetchRestaurants || fetchRestaurants.length === 0 || fetchRestaurants.includes(ALL_VALUE)) {
      setFetchPermission(false);
      return;
    }

    if (superAdmin?.role === 'admin') {
      setFetchPermission(true);
      return;
    }

    let hasPermission = false;

    fetchRestaurants?.forEach((id) => {
      const restaurant = restaurants?.find(r => r.id === id);
      if (restaurant && strictCanScope(ability, 'fetch_and_imports', 'Restaurant', `${restaurant?.id}`)) {
        hasPermission = true;
      }
    });

    setFetchPermission(hasPermission);
  }, [restaurants, superAdmin, ability])

  useEffect(() => {
    if (filters?.restaurant_ids && !filters?.restaurant_ids?.includes(ALL_VALUE) && filters?.restaurant_ids?.length > 0) {
      checkFetchPermission(filters.restaurant_ids);
    } else {
      setFetchPermission(false);
    }
  }, [filters?.restaurant_ids, restaurants, ability]);

  const handleExportCsv = async () => {
    try {
      setCsvLoading(true);
      const csvFilters = { ...buildFormattedFilters(appliedFilters) };
      if (csvFilters.restaurant_group_id === ALL_VALUE) {
        delete csvFilters.restaurant_group_id;
      }
      if (csvFilters.restaurant_ids?.includes(ALL_VALUE)) {
        delete csvFilters.restaurant_ids;
      }
      if (csvFilters.network_guest_name === ALL_VALUE) {
        delete csvFilters.network_guest_name;
      }
      if (csvFilters.has_ot_discount === ALL_VALUE) {
        delete csvFilters.has_ot_discount;
      }

      const query = buildQueryParams(csvFilters)
      const url = `${process.env.REACT_APP_API_BASE_URL}/v1/admin/csv_reports/export?report=employee_time_entries&${query}`;

      const { response } = await authenticatedFetch(url, {
        method: 'GET',
        defaultErrorMessage: 'Failed to download CSV'
      });

      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);

      const a = document.createElement('a');
      a.href = downloadUrl;
      let date = new Date
      let filename = `employee_time_entries_${dayjs(date).format('YYYY-MM-DD')}.csv`

      a.download = filename;
      a.click();

      window.URL.revokeObjectURL(downloadUrl);

      showNotification('CSV downloaded successfully', 'success');
    } catch (error) {
      console.error('CSV download error:', error);
      showNotification(error?.message || 'Failed to download CSV', 'error');
    } finally {
      setCsvLoading(false);
    }
  }

  const columns = useMemo(() => {
    const columnOptions = [
      { key: "employee_name", sortable: true },
      { key: "anomalies", sortable: true },
      { key: "location", sortable: true },
      { key: "job_title", sortable: true },
      { key: "date", sortable: true },
      { key: "time_in", sortable: true },
      { key: "time_out", sortable: true },
      { key: "regular_hours", sortable: true },
      { key: "overtime_hours", sortable: true },
      { key: "total_hours_worked", sortable: true },
      { key: "hourly_wage", sortable: true },
      { key: "total_before_discount", label: 'Total Before Perq', sortable: true },
      { key: "discounts", label: 'Perqs', sortable: true },
      { key: "ownerstable_discount_checkin_code", label: 'OwnersTable Perq Check-in Code', sortable: true }
    ];

    return columnOptions?.map((column) => ({
      key: column.key,
      label: column.label || camelize(column.key),
      sortable: column.sortable
    }));
  }, []);

  const rows = useMemo(() => {
    if (!employeeTimeEntries?.data) return [];

    return employeeTimeEntries.data.map((record) => ({
      employee_name: record?.employee_name,
      anomalies: {
        display: (
          <React.Fragment>
            {record?.auto_clocked_out && <Chip
              sx={{
                backgroundColor: '#1976d2',
                color: '#ffffff',
                "&:hover": {
                  backgroundColor: "#1976d2",
                  color: '#ffffff'
                },
              }}
              label={'AUTO CLOCK-OUT'}
            />}
          </React.Fragment>
        ),
        sortValue: record?.auto_clocked_out
      },
      location: record?.location,
      job_title: {
        display: (
          <RenderJobList data={record?.job_title ? [record.job_title] : []} />
        ),
        sortValue: record?.job_title?.title
      },
      date: moment.parseZone(record?.business_date).format("MM/DD/YYYY"),
      time_in: formatTime(record?.in_time, record?.time_zone_abbr),
      time_out: formatTime(record?.out_time, record?.time_zone_abbr),
      total_hours_worked: formatDecimal(record?.total_hours_worked),
      regular_hours: formatDecimal(record?.regular_hours),
      overtime_hours: formatDecimal(record?.overtime_hours),
      hourly_wage: formatCurrency(record?.hourly_wage),
      total_before_discount: `$${Number(record?.total_before_discount).toFixed(2)}`,
      discounts: `$${Number(record?.discounts).toFixed(2)}`,
      ownerstable_discount_checkin_code: {
        display: (
          <RenderList data={record?.check_in_code} />
        ),
        sortValue: record?.check_in_code?.[0]
      },
    }));
  }, [employeeTimeEntries?.data, formatTime, formatDecimal, formatCurrency]);

  const handleSortChange = useCallback((nextSortBy, nextSortOrder) => {
    setSortBy(nextSortBy)
    setSortOrder(nextSortOrder)
    setPage(1)
  }, [])

  const MemoizedIndexTable = React.memo(IndexTable);

  return (
    <React.Fragment>
      <Box
        sx={{
          height: "100%",
          width: "100%",
          overflowY: 'scroll',
          overflowX: 'hidden',
        }}
      >
        <StyledBreadCrumbContainer>
          <Breadcrumbs>
            <NavigableBreadcrumbLink LinkComponent={StyledLink} label="Home" to="/admin/dashboard" />
            <StyledLink color={"#515152"}>Reports</StyledLink>
            <StyledLink color={"#515152"}>Employee Reports</StyledLink>
            <StyledLink color={"#515152"}>Employee Shift Report</StyledLink>
          </Breadcrumbs>
        </StyledBreadCrumbContainer>

        <FilterContainer>
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              justifyContent: "space-between",
              alignItems: { xs: "flex-start", md: "center" },
              gap: { xs: 2, md: 0 }
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                gap: { xs: 2, sm: "18px" },
                flexWrap: "wrap",
                width: { xs: "100%", md: "auto" },
                flex: 1
              }}
            >
              <React.Fragment>
                <Typography
                  variant="body2"
                  sx={{
                    ...viewTypographyStyles,
                    alignSelf: { xs: "flex-start", sm: "center" },
                    whiteSpace: "nowrap"
                  }}
                >
                  View
                </Typography>

                <FormControl
                  size="small"
                  sx={{
                    width: { xs: "100%", sm: "auto" },
                    minWidth: { xs: "100%", sm: "160px" }
                  }}
                >
                  <StyledInputLabel>Date Range</StyledInputLabel>
                  <Select
                    value={filters.dateFilter || ''}
                    renderValue={() => filters?.dateFilter}
                    label='Date Range'
                    size="small"
                    MenuProps={{
                      anchorOrigin: {
                        vertical: "bottom",
                        horizontal: "left",
                      },
                      transformOrigin: {
                        vertical: "top",
                        horizontal: "left",
                      },
                      PaperProps: { sx: { width: "230px" } },
                      autoFocus: false,
                    }}
                    sx={{
                      ...selectBaseStyles,
                      minWidth: { xs: "100%", sm: "160px" },
                      borderColor: "#2b4fb9",
                    }}
                    onChange={(e) => handleFilterChange("dateFilter", e.target.value)}
                  >
                    {Days.map((value) => (
                      <StyledMenuItem
                        value={value}
                        key={value}
                      >
                        {
                          filters?.dateFilter === value ? (
                            <SelectedMenuItem data={value} />
                          ) : (
                            <>
                              {value}
                            </>
                          )
                        }
                      </StyledMenuItem>
                    ))}
                  </Select>
                </FormControl>
                {filters.dateFilter === "Custom Date" && (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      flexWrap: { xs: "wrap", sm: "nowrap" },
                      width: { xs: "100%", sm: "auto" }
                    }}
                  >
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <Typography variant="body2" sx={viewTypographyStyles}>
                        Days
                      </Typography>
                      <DatePicker
                        format="MM-DD-YYYY"
                        sx={{
                          ...datePickerStyles,
                          width: { xs: "100%", sm: "140px", lg: '160px' }
                        }}
                        slotProps={{ textField: { size: "small" } }}
                        value={filters.customDateFrom || null}
                        onChange={(date) =>
                          handleFilterChange("customDateFrom", date)
                        }
                      />
                      <Typography variant="body2" sx={viewTypographyStyles}>
                        through
                      </Typography>
                      <DatePicker
                        sx={{
                          ...datePickerStyles,
                          width: { xs: "100%", sm: "140px", lg: '160px' }
                        }}
                        format="MM-DD-YYYY"
                        slotProps={{ textField: { size: "small" } }}
                        value={filters.customDateTo || null}
                        onChange={(date) =>
                          handleFilterChange("customDateTo", date)
                        }
                      />
                    </LocalizationProvider>
                  </Box>
                )}

                <FormControl
                  size="small"
                  sx={{
                    width: { xs: "100%", sm: "auto" },
                    minWidth: { xs: "100%", sm: "160px" }
                  }}
                >
                  <StyledInputLabel>Restaurant Group</StyledInputLabel>
                  <Select
                    size="small"
                    label="Restaurant Group"
                    MenuProps={{
                      anchorOrigin: {
                        vertical: "bottom",
                        horizontal: "left",
                      },
                      transformOrigin: {
                        vertical: "top",
                        horizontal: "left",
                      },
                      PaperProps: {
                        sx: { maxHeight: "40vh" },
                        ref: restaurantGroupMenuRef,
                      },
                      autoFocus: false,
                      MenuListProps: { sx: { pt: 0 } },
                    }}
                    sx={{
                      ...selectBaseStyles,
                      minWidth: { xs: "100%", sm: "160px" },
                      width: { xs: "100%", sm: "160px" },
                      borderColor: "#2b4fb9",
                    }}
                    onChange={handleRestaurantGroupChange}
                    onClose={() => setTimeout(() => setRestaurantGroupSearch(''), 1000)}
                    value={hasSingleAvailableRestaurantGroup
                      ? getSingleItemValue(restaurantGroups)?.label
                      : (filters?.restaurant_group_id || ALL_VALUE)}
                    renderValue={() => {
                      if (hasSingleAvailableRestaurantGroup) {
                        return getSingleItemValue(restaurantGroups)?.value || 'All';
                      }
                      return getRestaurantGroupName(filters?.restaurant_group_id);
                    }}
                    endAdornment={
                      filters?.restaurant_group_id !== ALL_VALUE && !hasSingleAvailableRestaurantGroup ? (
                        <InputAdornment position="end" sx={{ position: 'absolute', right: 25 }}>
                          <IconButton
                            edge='end'
                            size="small"
                            disableRipple
                            onClick={handleRestaurantGroupClear}
                          >
                            <ClearIcon sx={{ fontSize: '20px' }} />
                          </IconButton>
                        </InputAdornment>
                      ) : null
                    }
                  >
                    <DropdownSearchBar changeSearchText={setRestaurantGroupSearch} placeholder={'Search restaurant group'} isFetching={restaurantGroupFetching} />
                    {/* All option */}
                    {!hasSingleAvailableRestaurantGroup && (<StyledMenuItem
                      key={ALL_VALUE}
                      value={ALL_VALUE}
                    >
                      {filters?.restaurant_group_id === ALL_VALUE ? <SelectedMenuItem data="All" /> : "All"}
                    </StyledMenuItem>
                    )}
                    {restaurantGroups.map((res) => (
                      <StyledMenuItem
                        key={res?.label}
                        value={res?.label}
                      >
                        {
                          hasSingleAvailableRestaurantGroup || filters?.restaurant_group_id === res?.label ? <SelectedMenuItem data={res?.value} /> : res?.value
                        }
                      </StyledMenuItem>
                    ))}
                    <DropdownInfiniteLoader
                      hasNextPage={hasNextRestaurantGroupsPage}
                      targetRef={restaurantGroupLoaderRef}
                    />
                  </Select>
                </FormControl>

                <FormControl
                  size="small"
                  sx={{
                    width: { xs: "100%", sm: "auto" },
                    minWidth: { xs: "100%", sm: "200px" }
                  }}
                >
                  <StyledInputLabel>Restaurant</StyledInputLabel>
                  <Select
                    size="small"
                    multiple
                    label="Restaurant"
                    MenuProps={{
                      anchorOrigin: {
                        vertical: "bottom",
                        horizontal: "left",
                      },
                      transformOrigin: {
                        vertical: "top",
                        horizontal: "left",
                      },
                      PaperProps: {
                        sx: { maxHeight: "40vh" },
                        ref: restaurantMenuRef,
                      },
                      autoFocus: false,
                      MenuListProps: { sx: { pt: 0 } },
                    }}
                    sx={{
                      ...selectBaseStyles,
                      minWidth: { xs: "100%", sm: "200px" },
                      borderColor: "#2b4fb9",
                    }}
                    onChange={handleRestaurantChange}
                    onClose={() => setTimeout(() => setRestaurantSearch(''), 1000)}
                    value={hasSingleAvailableRestaurant
                      ? [getSingleItemValue(restaurants)?.id]
                      : (filters?.restaurant_ids || [ALL_VALUE])}
                    renderValue={() => {
                      if (hasSingleAvailableRestaurant) {
                        return getSingleItemValue(restaurants)?.name || 'All';
                      }
                      return getRestaurantDisplayValue();
                    }}
                    endAdornment={
                      !filters?.restaurant_ids?.includes(ALL_VALUE) && !hasSingleAvailableRestaurant ? (
                        <InputAdornment position="end" sx={{ position: 'absolute', right: 25 }}>
                          <IconButton
                            edge='end'
                            size="small"
                            disableRipple
                            onClick={handleRestaurantClear}
                          >
                            <ClearIcon sx={{ fontSize: '20px' }} />
                          </IconButton>
                        </InputAdornment>
                      ) : null
                    }
                  >
                    <DropdownSearchBar changeSearchText={setRestaurantSearch} placeholder={'Search restaurant'} isFetching={restaurantFetching} />
                    {/* All option */}
                    {!hasSingleAvailableRestaurant && (<StyledMenuItem
                      key={ALL_VALUE}
                      value={ALL_VALUE}
                      disabled={!filters?.restaurant_ids?.includes(ALL_VALUE)}
                    >
                      {filters?.restaurant_ids?.includes(ALL_VALUE) ? <SelectedMenuItem data="All" /> : "All"}
                    </StyledMenuItem>
                    )}
                    {restaurants.map((res) => (
                      <StyledMenuItem
                        key={res?.id}
                        value={res?.id}
                      >
                        {
                          hasSingleAvailableRestaurant || filters?.restaurant_ids?.includes(res?.id) ? <SelectedMenuItem data={res?.name} /> : res?.name
                        }
                      </StyledMenuItem>
                    ))}
                    <DropdownInfiniteLoader
                      hasNextPage={hasNextRestaurantsPage}
                      targetRef={restaurantLoaderRef}
                    />
                  </Select>
                </FormControl>

                <FormControl
                  size="small"
                  sx={{
                    width: { xs: "100%", sm: "auto" },
                    minWidth: { xs: "100%", sm: "200px" }
                  }}
                >
                  <StyledInputLabel>Employees</StyledInputLabel>
                  <Select
                    value={hasSingleAvailableEmployee
                      ? getSingleItemValue(employeesList)?.value
                      : (filters?.network_guest_name || ALL_VALUE)}
                    label="Employees"
                    size="small"
                    MenuProps={{
                      anchorOrigin: {
                        vertical: "bottom",
                        horizontal: "left",
                      },
                      transformOrigin: {
                        vertical: "top",
                        horizontal: "left",
                      },
                      PaperProps: {
                        sx: { maxHeight: "40vh" },
                        ref: employeeMenuRef,
                      },
                      autoFocus: false,
                      MenuListProps: { sx: { pt: 0 } },
                    }}
                    sx={{
                      ...selectBaseStyles,
                      minWidth: { xs: "100%", sm: "200px" },
                      borderColor: "#2b4fb9",
                    }}
                    onChange={handleEmployeeChange}
                    onClose={() => setEmployeeSearch("")}
                    renderValue={() => {
                      if (hasSingleAvailableEmployee) {
                        return getSingleItemValue(employeesList)?.label || 'All';
                      }
                      return getEmployeeName(filters?.network_guest_name);
                    }}
                    endAdornment={
                      filters?.network_guest_name !== ALL_VALUE && !hasSingleAvailableEmployee ? (
                        <InputAdornment position="end" sx={{ position: 'absolute', right: 25 }}>
                          <IconButton
                            edge='end'
                            size="small"
                            disableRipple
                            onClick={handleEmployeeClear}
                          >
                            <ClearIcon sx={{ fontSize: '20px' }} />
                          </IconButton>
                        </InputAdornment>
                      ) : null
                    }
                  >
                    <DropdownSearchBar changeSearchText={setEmployeeSearch} placeholder={'Search employee'} isFetching={employeeFetching} />
                    {/* All option */}
                    {!hasSingleAvailableEmployee && (<StyledMenuItem
                      key={ALL_VALUE}
                      value={ALL_VALUE}
                    >
                      {filters?.network_guest_name === ALL_VALUE ? <SelectedMenuItem data="All" /> : "All"}
                    </StyledMenuItem>
                    )}
                    {employeesList.map((employee, index) => (
                        <StyledMenuItem
                          key={index}
                          value={employee.value}
                        >
                          {
                            hasSingleAvailableEmployee || filters?.network_guest_name === employee?.value ? <SelectedMenuItem data={employee?.label} /> : employee?.label
                          }
                        </StyledMenuItem>
                      ))}
                    <DropdownInfiniteLoader
                      hasNextPage={hasNextEmployeesPage}
                      targetRef={employeeLoaderRef}
                    />
                  </Select>
                </FormControl>

                <FormControl
                  size="small"
                  sx={{
                    width: { xs: "100%", sm: "auto" },
                    minWidth: { xs: "100%", sm: "160px" }
                  }}
                >
                  <StyledInputLabel>Perqs</StyledInputLabel>
                  <Select
                    renderValue={() => filters?.has_ot_discount === null ? 'Perqs' : getDiscountLabel(filters?.has_ot_discount)}
                    size="small"
                    MenuProps={{
                      anchorOrigin: {
                        vertical: "bottom",
                        horizontal: "left",
                      },
                      transformOrigin: {
                        vertical: "top",
                        horizontal: "left",
                      },
                      PaperProps: { sx: { maxHeight: "40vh", width: '230px' } },
                      autoFocus: false,
                    }}
                    label="Perqs"
                    sx={{
                      ...selectBaseStyles,
                      minWidth: { xs: "100%", sm: "160px" },
                      borderColor: "#2b4fb9",
                    }}
                    value={filters?.has_ot_discount ?? ALL_VALUE}
                    onChange={handleOwnersTableDiscount}
                  >
                    <StyledMenuItem value={ALL_VALUE}>
                      {
                        filters?.has_ot_discount === ALL_VALUE ? <SelectedMenuItem data={'All'} /> : 'All'
                      }
                    </StyledMenuItem>
                    <StyledMenuItem value={true}>
                      {
                        filters?.has_ot_discount === true ? <SelectedMenuItem data={'OwnersTable'} /> : 'OwnersTable'
                      }
                    </StyledMenuItem>
                  </Select>
                </FormControl>
              </React.Fragment>
            </Box>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                alignSelf: { xs: "flex-start", md: "center" },
                mt: { xs: 1, md: 0 },
                width: { xs: "100%", md: "auto" },
                justifyContent: { xs: "flex-start", md: "flex-end" }
              }}
            >
              <ResetButton
                onClick={handleReset}
                filtersChanged={filtersChanged}
                status={isFetching}
              />
              <UpdateButton
                onClick={handleChange}
                filtersChanged={filtersChanged}
                status={isFetching}
                disabled={isUpdateDisabled}
              />
            </Box>
          </Box>
        </FilterContainer>

        <MenuHeading report={false}>Employee Shift Report</MenuHeading>

        <ActionContainer>
          <Button
            sx={{ background: "#2b4fb9" }}
            variant="contained"
            onClick={fetchData}
            component="label"
            data-testid='employee-fetch-btn'
            disabled={!fetchPermission || isLoading}
          >
            {isLoading ? <LoadingSpinner isLoading={true} /> : <CachedIcon />}
          </Button>
        </ActionContainer>

        {isFetching ? (
          <Loader />
        ) : (
          <MemoizedIndexTable
            columns={columns}
            data={rows}
            tableId="employee-time-entries"
            type="entries"
            sortMode="server"
            sortBy={sortBy}
            sortOrder={sortOrder}
            onSortChange={handleSortChange}
            itemsPerPage={itemsPerPage}
            setItemsPerPage={setItemsPerPage}
            page={page}
            setPage={setPage}
            pageCount={employeeTimeEntries?.pageCount}
            report={false}
            showDownloadButton={true}
            disableDownloadButton={employeeTimeEntries?.data?.length === 0 || csvLoading}
            handleExportCsv={handleExportCsv}
            csvLoading={csvLoading}
          />
        )}

      </Box>
    </React.Fragment>
  );
};
